import Phaser from 'phaser';
import { GAME, ASSETS } from '../config';
import { BgmPlayer } from '../audio/bgm-player';
import { PLAYER_TANK_SPRITE_URLS } from '../entities/sprite-keys';

/**
 * 開場標題畫面。
 *
 * 視覺層次（由後往前）:
 *   1. 夜空漸層（Graphics,深紫→暗藍）+ 星點
 *   2. 遠景戰場剪影(地平線丘陵 + 城堡塔樓 = 「要塞」意象)
 *   3. 主視覺:一台主角戰車 sprite(celestial,缺圖時 fallback Graphics)
 *   4. 標題「永恆要塞」(描邊 + 發光 + 漂浮動畫)+ 副標
 *   5. 「⚔ 開始冒險」圓角發光按鈕(脈動 + hover 放大)
 *
 * 鐵則:點按鈕 / Enter / 空白 都會 dispatch `ef:start-game`,不破壞進入流程。
 */
export class TitleScene extends Phaser.Scene {
  private music!: BgmPlayer;
  private started = false;
  /** DOM 層備援點擊處理器(掛在 Phaser canvas 上),shutdown 時移除。 */
  private domTapHandler: ((e: PointerEvent) => void) | null = null;

  constructor() { super('TitleScene'); }

  preload(): void {
    // 主視覺戰車(celestial)+ 夜空底圖,缺檔時程式生成 fallback。
    this.load.image(ASSETS.tankPlayerCelestial, PLAYER_TANK_SPRITE_URLS.celestial);
    this.load.image(ASSETS.hStageCelestial, 'assets/backgrounds/h-stages/h-stage-celestial.png');
  }

  create(): void {
    const W = GAME.width;
    const H = GAME.height;
    // 防禦(boss 2026-06-16「中途離開回標題無法操作」):進 TitleScene 一律清掉殘留的
    // 戰鬥觸控 HUD(左下搖桿 + 右下 技/⚡)。任何路徑到標題(暫停→離開 / 結算→回標題)都
    // 不該帶著戰鬥 overlay,否則它蓋在「開始冒險」鈕上攔截觸控 → 整頁按不動。
    try { document.querySelector('.touch-controls')?.remove(); } catch { /* noop */ }
    this.cameras.main.setBackgroundColor('#0a0420');

    this.drawSky(W, H);
    this.drawStars(W, H);
    this.drawSkyline(W, H);
    this.drawHeroTank(W, H);
    this.drawTitle(W, H);
    this.drawStartButton(W, H);
    this.setupKeyboard();

    this.add.text(W - 10, H - 10, 'v0.5 by Pata', {
      fontSize: '12px',
      color: '#8a7fb0',
    }).setOrigin(1, 1);

    this.music = new BgmPlayer();
    this.music.start(); // synth 起步
    this.music.playTitleBgm();

    this.events.on('shutdown', () => {
      this.music.stop();
      if (this.domTapHandler) {
        this.game?.canvas?.removeEventListener('pointerup', this.domTapHandler);
        this.domTapHandler = null;
      }
      // 允許再次進入 title 時重新觸發(例如 ResultScene → 回 title)。
      this.started = false;
    });
  }

  /** 夜空漸層:深紫 → 暗藍,底部透出一點地平線暖光。 */
  private drawSky(W: number, H: number): void {
    const g = this.add.graphics();
    const top = Phaser.Display.Color.ValueToColor('#1a0533');
    const bot = Phaser.Display.Color.ValueToColor('#06122e');
    const steps = 64;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const c = Phaser.Display.Color.Interpolate.ColorWithColor(top, bot, 100, t * 100);
      g.fillStyle(Phaser.Display.Color.GetColor(c.r, c.g, c.b), 1);
      g.fillRect(0, (H / steps) * i, W, H / steps + 1);
    }
    // 地平線暖光(遠方戰火)
    const glow = this.add.graphics();
    glow.fillStyle(0xff7b3d, 0.18);
    glow.fillEllipse(W / 2, H * 0.78, W * 1.2, 160);
    glow.fillStyle(0xffb74d, 0.12);
    glow.fillEllipse(W / 2, H * 0.8, W * 0.8, 90);
  }

  /** 隨機星點,部分加閃爍 tween。 */
  private drawStars(W: number, H: number): void {
    const rnd = new Phaser.Math.RandomDataGenerator(['eternal-fortress']);
    for (let i = 0; i < 90; i++) {
      const x = rnd.between(0, W);
      const y = rnd.between(0, Math.floor(H * 0.6));
      const r = rnd.realInRange(0.6, 1.8);
      const star = this.add.circle(x, y, r, 0xffffff, rnd.realInRange(0.4, 1));
      if (rnd.frac() < 0.35) {
        this.tweens.add({
          targets: star,
          alpha: 0.15,
          duration: rnd.between(900, 2200),
          yoyo: true,
          repeat: -1,
          delay: rnd.between(0, 1500),
        });
      }
    }
  }

  /** 遠景戰場剪影:起伏地平線 + 中央要塞城堡塔樓。 */
  private drawSkyline(W: number, H: number): void {
    const horizon = H * 0.82;
    const g = this.add.graphics();

    // 後排丘陵(較淡)
    g.fillStyle(0x1b1140, 0.9);
    g.beginPath();
    g.moveTo(0, horizon + 18);
    g.lineTo(0, horizon - 10);
    for (let x = 0; x <= W; x += 40) {
      g.lineTo(x, horizon - 10 - Math.sin(x * 0.012) * 16 - Math.cos(x * 0.03) * 8);
    }
    g.lineTo(W, H);
    g.lineTo(0, H);
    g.closePath();
    g.fillPath();

    // 前排地面(較深)
    g.fillStyle(0x0d0826, 1);
    g.fillRect(0, horizon + 12, W, H - horizon);

    // 中央要塞:城牆 + 三塔(剪影)
    const cx = W / 2;
    const base = horizon + 14;
    const wall = this.add.graphics();
    wall.fillStyle(0x140d33, 1);
    // 城牆主體
    wall.fillRect(cx - 110, base - 70, 220, 70);
    // 城垛
    for (let i = -110; i < 110; i += 30) {
      wall.fillRect(cx + i, base - 82, 16, 14);
    }
    // 左右側塔
    this.drawTower(wall, cx - 130, base, 40, 120);
    this.drawTower(wall, cx + 90, base, 40, 120);
    // 中央高塔
    this.drawTower(wall, cx - 28, base, 56, 170, true);
  }

  private drawTower(
    g: Phaser.GameObjects.Graphics,
    x: number, base: number, w: number, h: number, flag = false,
  ): void {
    g.fillStyle(0x191040, 1);
    g.fillRect(x, base - h, w, h);
    // 城垛
    for (let i = 0; i < w; i += 14) {
      g.fillRect(x + i, base - h - 10, 8, 12);
    }
    // 頂部尖塔
    g.fillTriangle(x - 4, base - h - 6, x + w + 4, base - h - 6, x + w / 2, base - h - 34);
    if (flag) {
      // 旗杆 + 旗(暖色,唯一亮點,呼應戰旗)
      g.fillStyle(0xffd54f, 1);
      g.fillRect(x + w / 2 - 1, base - h - 64, 2, 30);
      g.fillTriangle(
        x + w / 2 + 1, base - h - 62,
        x + w / 2 + 1, base - h - 48,
        x + w / 2 + 24, base - h - 55,
      );
      g.fillStyle(0x191040, 1);
    }
    // 塔窗(暖光)
    g.fillStyle(0xffb74d, 0.55);
    g.fillRect(x + w / 2 - 4, base - h * 0.55, 8, 12);
    g.fillStyle(0x191040, 1);
  }

  /** 主視覺戰車:sprite 優先,缺圖時用 Graphics 畫個簡易坦克剪影。 */
  private drawHeroTank(W: number, H: number): void {
    const x = W / 2;
    const y = H * 0.6;

    if (this.textures.exists(ASSETS.tankPlayerCelestial)) {
      const tank = this.add.image(x, y, ASSETS.tankPlayerCelestial).setOrigin(0.5, 0.5);
      const target = 220;
      tank.setDisplaySize(target, target * (tank.height / tank.width || 1));
      // 落地陰影
      const shadow = this.add.ellipse(x, y + tank.displayHeight * 0.42, tank.displayWidth * 0.7, 26, 0x000000, 0.45);
      shadow.setDepth(tank.depth - 1);
      // 輕微漂浮 + 陰影縮放,讓主視覺有生氣
      this.tweens.add({
        targets: tank,
        y: y - 10,
        duration: 1600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: shadow,
        scaleX: 0.85,
        alpha: 0.3,
        duration: 1600,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      return;
    }

    // Fallback:Graphics 坦克剪影
    const g = this.add.graphics();
    g.fillStyle(0x000000, 0.4);
    g.fillEllipse(x, y + 60, 150, 24);
    g.fillStyle(0x3949ab, 1);
    g.fillRoundedRect(x - 80, y - 10, 160, 60, 10); // 履帶/車身
    g.fillStyle(0x5c6bc0, 1);
    g.fillRoundedRect(x - 45, y - 45, 90, 45, 8); // 砲塔
    g.fillStyle(0x9fa8da, 1);
    g.fillRect(x + 35, y - 30, 70, 12); // 砲管
    g.fillStyle(0x90caf9, 1);
    g.fillCircle(x - 5, y - 22, 8); // 觀察窗
  }

  /** 標題「永恆要塞」:多層描邊 + 發光 + 漂浮;副標跟隨。 */
  private drawTitle(W: number, _H: number): void {
    const cx = W / 2;
    const ty = 130;

    const title = this.add.text(cx, ty, '永恆要塞', {
      fontSize: '92px',
      color: '#ffe082',
      fontFamily: 'serif',
      fontStyle: 'bold',
      stroke: '#3d1a66',
      strokeThickness: 12,
    }).setOrigin(0.5);
    title.setShadow(0, 6, '#1a0533', 12, true, true);
    // 金色發光描邊(內層)
    title.setPadding(8);

    // 發光:額外一層放在後面、模糊感用 alpha + scale tween 模擬光暈
    const glow = this.add.text(cx, ty, '永恆要塞', {
      fontSize: '92px',
      color: '#ffd54f',
      fontFamily: 'serif',
      fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0.25).setScale(1.06);
    glow.setDepth(title.depth - 1);
    this.tweens.add({
      targets: glow,
      alpha: 0.45,
      scale: 1.1,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // 整組標題輕微漂浮
    this.tweens.add({
      targets: [title, glow],
      y: ty + 8,
      duration: 2600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const sub = this.add.text(cx, ty + 78, '⚔ 五門派修煉者的試煉 ⚔', {
      fontSize: '26px',
      color: '#c5cae9',
      fontFamily: 'serif',
      stroke: '#1a0533',
      strokeThickness: 4,
    }).setOrigin(0.5);
    sub.setShadow(0, 2, '#000000', 4, true, true);
  }

  /** 「⚔ 開始冒險」圓角發光按鈕:脈動 + hover 放大,可鍵盤觸發。 */
  private drawStartButton(W: number, H: number): void {
    const bx = W / 2;
    const by = H * 0.86;
    const bw = 300;
    const bh = 76;

    const container = this.add.container(bx, by);

    // 外圈光暈
    const halo = this.add.graphics();
    halo.fillStyle(0xffd54f, 0.18);
    halo.fillRoundedRect(-bw / 2 - 14, -bh / 2 - 14, bw + 28, bh + 28, 26);

    // 按鈕本體(漸層感:兩層圓角矩形)
    const bg = this.add.graphics();
    const drawBg = (c1: number, c2: number) => {
      bg.clear();
      bg.fillStyle(c1, 1);
      bg.fillRoundedRect(-bw / 2, -bh / 2, bw, bh, 18);
      bg.fillStyle(c2, 1);
      bg.fillRoundedRect(-bw / 2, -bh / 2, bw, bh * 0.5, { tl: 18, tr: 18, bl: 0, br: 0 });
      bg.lineStyle(3, 0xfff8e1, 0.9);
      bg.strokeRoundedRect(-bw / 2, -bh / 2, bw, bh, 18);
    };
    drawBg(0xf9a825, 0xffd54f);

    const label = this.add.text(0, 0, '⚔ 開始冒險', {
      fontSize: '34px',
      color: '#3d1a00',
      fontFamily: 'sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    container.add([halo, bg, label]);
    container.setSize(bw, bh);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-bw / 2, -bh / 2, bw, bh),
      Phaser.Geom.Rectangle.Contains,
    );
    this.input.setDefaultCursor('default');

    // 脈動(光暈呼吸)吸引點擊
    this.tweens.add({
      targets: halo,
      alpha: 0.05,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: container,
      scale: 1.04,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    container.on('pointerover', () => {
      drawBg(0xffb300, 0xffe082);
      this.input.setDefaultCursor('pointer');
      this.tweens.add({ targets: container, scale: 1.1, duration: 120, ease: 'Back.easeOut' });
    });
    container.on('pointerout', () => {
      drawBg(0xf9a825, 0xffd54f);
      this.input.setDefaultCursor('default');
      this.tweens.add({ targets: container, scale: 1.0, duration: 120 });
    });
    container.on('pointerdown', () => {
      this.tweens.add({ targets: container, scale: 0.96, duration: 80, yoyo: true });
    });
    container.on('pointerup', () => this.startGame());

    // 真機觸控備援 — Phaser 3.90 在 Scale.FIT「letterbox」(canvas 有上下/左右
    // 黑邊、canvasBounds 偏移非 0)時,**touch** 事件的命中測試會算錯座標,導致
    // container 的 pointerup 在手機/平板上根本不觸發(滑鼠正常)。實測:390px 滿版
    // canvas(偏移 0)OK,但 820/1280 letterbox 觸控全 miss。改在 Phaser canvas
    // 掛一層 DOM pointerup,把 client 座標換算回遊戲座標,落在按鈕矩形內就 startGame。
    // startGame() 內的 `started` 旗標確保即使桌機兩條路徑都觸發也只 dispatch 一次。
    this.attachDomTapFallback(bx, by, bw, bh);

    // 鍵盤提示
    this.add.text(W / 2, by + bh / 2 + 26, '按 Enter 或 空白鍵開始', {
      fontSize: '16px',
      color: '#9fa8da',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
  }

  /**
   * 真機觸控備援:在 Phaser canvas 上掛 DOM pointerup,自行把 client 座標換算
   * 成遊戲座標並比對按鈕矩形,繞過 Phaser 3.90 touch + letterbox 命中測試 bug。
   * 涵蓋滑鼠 / 觸控 / 觸控筆(pointer events 統一)。
   */
  private attachDomTapFallback(bx: number, by: number, bw: number, bh: number): void {
    const canvas = this.game?.canvas;
    if (!canvas || typeof canvas.addEventListener !== 'function') return;
    const left = bx - bw / 2;
    const right = bx + bw / 2;
    const top = by - bh / 2;
    const bottom = by + bh / 2;
    const handler = (e: PointerEvent): void => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const sm = this.scale;
      const gx = (e.clientX - rect.left) * (sm.gameSize.width / rect.width);
      const gy = (e.clientY - rect.top) * (sm.gameSize.height / rect.height);
      if (gx >= left && gx <= right && gy >= top && gy <= bottom) {
        this.startGame();
      }
    };
    canvas.addEventListener('pointerup', handler);
    this.domTapHandler = handler;
  }

  /** Enter / 空白鍵也能開始。 */
  private setupKeyboard(): void {
    const kb = this.input.keyboard;
    if (!kb) return;
    kb.on('keydown-ENTER', () => this.startGame());
    kb.on('keydown-SPACE', () => this.startGame());
  }

  /** 鐵則:統一從這裡 dispatch `ef:start-game`(只觸發一次)。 */
  private startGame(): void {
    if (this.started) return;
    this.started = true;
    window.dispatchEvent(new CustomEvent('ef:start-game'));
  }
}
